import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
} from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "./InteractiveMap.css";
import {
  Checkbox,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import apiConfig, { hereMapApiKey } from "../../../../service/apiConfig";
import { ZoomInMap, ZoomOutMap } from "@mui/icons-material";
import { capturePolygonImage } from "./util";

// HERE Maps API configuration
interface HereMapConfig {
  apiKey: string;
  baseUrl?: string;
}

interface PropertyArea {
  id: string;
  label: string;
  coordinates: [number, number][];
  color?: string;
  selected?: boolean;
  size?: number;
  originalFeatureIndex?: number;
}

interface InteractiveMapProps {
  center?: [number, number];
  zoom?: number;
  propertyAreas?: PropertyArea[];
  onAreaSizeCalculate?: (size: number) => void;
  hereConfig?: HereMapConfig;
  onToggleLeftSidebar?: () => void;
}

const InteractiveMap: React.FC<InteractiveMapProps> = ({
  center = [40.7128, -74.006],
  zoom = 15,
  propertyAreas = [],
  onAreaSizeCalculate,
  hereConfig,
  onToggleLeftSidebar,
}) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<L.Map | null>(null);
  const isMountedRef = useRef(true);

  const [selectedAreas, setSelectedAreas] = useState<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState(true);
  const [isMapReady, setIsMapReady] = useState(false);
  const [isSidebarVisible, setIsSidebarVisible] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isLeftSidebarVisible, setIsLeftSidebarVisible] = useState(true);
  const polygonLayersRef = useRef<{ [key: string]: L.Polygon }>({});
  const labelMarkersRef = useRef<{ [key: string]: L.Marker }>({});

  // Detect mobile device
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Default HERE Maps API configuration
  const defaultHereConfig: HereMapConfig = {
    apiKey: hereMapApiKey[process.env.NODE_ENV || "development"],
    baseUrl: apiConfig.hereMapApiUrl,
  };

  const config = { ...defaultHereConfig, ...hereConfig };
  const areasWithLabels = useMemo(() => {
    return propertyAreas.map((area) => {
      let prefix = "A";
      let areasForThisFeature: PropertyArea[] = [];
      let useContentBasedGrouping = false;

      // First, check if label contains back/front/side keywords (takes priority)
      if (area.label && typeof area.label === "string") {
        const labelLower = area.label.toLowerCase();
        if (labelLower.includes("back")) {
          prefix = "B";
          useContentBasedGrouping = true;
        } else if (labelLower.includes("front")) {
          prefix = "F";
          useContentBasedGrouping = true;
        } else if (labelLower.includes("side")) {
          prefix = "S";
          useContentBasedGrouping = true;
        }
      }

      if (useContentBasedGrouping) {
        // Group by prefix for correct numbering based on label content
        areasForThisFeature = propertyAreas.filter((a) => {
          if (!a.label || typeof a.label !== "string") return false;
          const l = a.label.toLowerCase();
          if (prefix === "B") return l.includes("back");
          if (prefix === "F") return l.includes("front");
          if (prefix === "S") return l.includes("side");
          return false;
        });
      } else {
        // Default grouping for areas without keywords
        areasForThisFeature = propertyAreas.filter((a) => {
          if (!a.label || typeof a.label !== "string") return false;
          const l = a.label.toLowerCase();
          return (
            !l.includes("back") && !l.includes("front") && !l.includes("side")
          );
        });
      }

      const indexInFeature =
        areasForThisFeature.findIndex((a) => a.id === area.id) + 1;
      const mapLabel = `${prefix}${indexInFeature}`;
      return {
        ...area,
        mapLabel,
      };
    });
  }, [propertyAreas]);

  const generatePolygonLabel = useCallback(
    (area: PropertyArea) => {
      const areaWithLabel = areasWithLabels.find((a) => a.id === area.id);
      return areaWithLabel?.mapLabel || "A1";
    },
    [areasWithLabels],
  );
  const createLabelMarker = useCallback(
    (area: PropertyArea, index: number, center: L.LatLng) => {
      const label = generatePolygonLabel(area);
      const labelIcon = L.divIcon({
        className: "polygon-label",
        html: `<div>${label}</div>`,
        iconSize: [30, 20],
        iconAnchor: [15, 10],
      });
      return L.marker(center, {
        icon: labelIcon,
        interactive: false,
      });
    },
    [generatePolygonLabel],
  );

  const onAreaSelect = useCallback(
    (areaId: string, areaSize: number, isAdding: boolean) => {
      if (onAreaSizeCalculate) {
        const totalSize = Array.from(selectedAreas).reduce((sum, id) => {
          const area = propertyAreas.find((area) => area.id === id);
          return sum + (area?.size || 0);
        }, 0);
        const newTotalSize = isAdding
          ? totalSize + areaSize
          : totalSize - areaSize;
        onAreaSizeCalculate(newTotalSize);
      }
    },
    [onAreaSizeCalculate, propertyAreas, selectedAreas],
  );

  // Select all areas when map loads
  useEffect(() => {
    if (propertyAreas.length > 0 && selectedAreas.size === 0) {
      const allAreaIds = propertyAreas.map((area) => area.id);
      setSelectedAreas(new Set(allAreaIds));

      const totalSize = propertyAreas.reduce(
        (sum, area) => sum + (area.size || 0),
        0,
      );
      onAreaSizeCalculate?.(totalSize);
    }
  }, [propertyAreas]);

  // Get HERE Maps satellite tile URL
  const getHereSatelliteUrl = useCallback(() => {
    return `${config.baseUrl}/base/mc/{z}/{x}/{y}/png8?apikey=${config.apiKey}&style=satellite.day`;
  }, [config.baseUrl, config.apiKey]);

  // Cleanup function
  const cleanupMap = useCallback(() => {
    isMountedRef.current = false;
    if (mapInstanceRef.current) {
      for (const polygon of Object.values(polygonLayersRef.current)) {
        mapInstanceRef.current.removeLayer(polygon);
      }
      for (const marker of Object.values(labelMarkersRef.current)) {
        mapInstanceRef.current.removeLayer(marker);
      }
      polygonLayersRef.current = {};
      labelMarkersRef.current = {};
      mapInstanceRef.current.remove();
      mapInstanceRef.current = null;
    }
  }, []);

  // Initialize map
  useEffect(() => {
    if (!mapRef.current || mapInstanceRef.current) {
      return;
    }

    isMountedRef.current = true;
    setIsLoading(true);

    const map = L.map(mapRef.current, {
      center,
      zoom,
      zoomControl: false,
      fadeAnimation: false,
      zoomAnimation: true,
      maxZoom: 19,
      minZoom: 18,
    });
    mapInstanceRef.current = map;

    L.control
      .zoom({
        position: "bottomright",
      })
      .addTo(map);

    const tileLayer = L.tileLayer(getHereSatelliteUrl(), {
      attribution: "",
      maxZoom: 19,
      minZoom: 18,
    });

    tileLayer.on("load", () => {
      if (isMountedRef.current) {
        setIsLoading(false);
        setIsMapReady(true);
      }
    });

    tileLayer.on("tileerror", (error) => {
      console.error("HERE Maps tile loading error:", error);
      if (isMountedRef.current) {
        setIsLoading(false);
      }
    });

    tileLayer.addTo(map);

    const handleResize = () => {
      setTimeout(() => {
        if (mapInstanceRef.current) {
          mapInstanceRef.current.invalidateSize({ animate: false });
        }
      }, 250);
    };
    window.addEventListener("resize", handleResize);

    return () => {
      isMountedRef.current = false;
      window.removeEventListener("resize", handleResize);
      cleanupMap();
    };
  }, [center, zoom, getHereSatelliteUrl, cleanupMap]);

  // Resize map when sidebar visibility changes
  useEffect(() => {
    if (mapInstanceRef.current) {
      setTimeout(() => {
        mapInstanceRef.current?.invalidateSize({ animate: false });
      }, 300);
    }
  }, [isSidebarVisible]);

  // Add or update property areas
  useEffect(() => {
    if (!isMapReady || !mapInstanceRef.current) return;

    for (const polygon of Object.values(polygonLayersRef.current)) {
      mapInstanceRef.current?.removeLayer(polygon);
    }
    for (const marker of Object.values(labelMarkersRef.current)) {
      mapInstanceRef.current?.removeLayer(marker);
    }
    polygonLayersRef.current = {};
    labelMarkersRef.current = {};

    if (propertyAreas.length === 0) return;

    const polygonsToFit: L.Polygon[] = [];

    for (let index = 0; index < propertyAreas.length; index++) {
      const area = propertyAreas[index];
      const isSelected = area.selected || selectedAreas.has(area.id);
      const polygon = L.polygon(area.coordinates, {
        color: area.color || "#ff7800",
        fillColor: area.color || "#ff7800",
        fillOpacity: isSelected ? 0.6 : 0.3,
        weight: isSelected ? 3 : 2,
      });

      polygon.on("click", () => {
        if (isMountedRef.current) {
          const isCurrentlySelected = selectedAreas.has(area.id);
          setSelectedAreas((prev) => {
            const newSet = new Set(prev);
            if (isCurrentlySelected) {
              newSet.delete(area.id);
            } else {
              newSet.add(area.id);
            }
            return newSet;
          });
          onAreaSelect(area.id, area.size || 0, !isCurrentlySelected);
        }
      });

      polygon.bindPopup(`<b>${area.label}</b>`);

      if (mapInstanceRef.current) {
        polygon.addTo(mapInstanceRef.current);
        polygonLayersRef.current[area.id] = polygon;
        polygonsToFit.push(polygon);
        const center = polygon.getBounds().getCenter();
        const labelMarker = createLabelMarker(area, index, center);
        labelMarker.addTo(mapInstanceRef.current);
        labelMarkersRef.current[area.id] = labelMarker;
      }
    }

    if (polygonsToFit.length > 0 && mapInstanceRef.current) {
      const group = new L.FeatureGroup(polygonsToFit);
      mapInstanceRef.current.fitBounds(group.getBounds(), {
        padding: [20, 20],
        animate: false,
      });
    }

    // Capture map image after polygons are rendered
    if (mapInstanceRef.current && propertyAreas.length > 0) {
      setTimeout(() => {
        if (mapInstanceRef.current) {
          capturePolygonImage(mapInstanceRef.current, (imageUrl) => {
            if (imageUrl) {
              globalThis.selectedPolygonImage = imageUrl;
            } else {
              console.error("Failed to capture polygon map image");
            }
          });
        }
      }, 1500);
    }
  }, [
    propertyAreas,
    isMapReady,
    onAreaSelect,
    selectedAreas,
    createLabelMarker,
  ]);

  // Update polygon styles when selection changes
  useEffect(() => {
    if (!isMapReady) return;
    for (const [areaId, polygon] of Object.entries(polygonLayersRef.current)) {
      const isSelected = selectedAreas.has(areaId);
      polygon.setStyle({
        fillOpacity: isSelected ? 0.6 : 0.3,
        weight: isSelected ? 3 : 2,
      });
    }
  }, [selectedAreas, isMapReady]);

  const handleAreaSelect = useCallback(
    (
      areaId: string,
      areaSize: number,
      e: React.ChangeEvent<HTMLInputElement>,
    ) => {
      const isChecked = e.target.checked;
      setSelectedAreas((prev) => {
        const newSet = new Set(prev);
        if (isChecked) {
          newSet.add(areaId);
        } else {
          newSet.delete(areaId);
        }
        return newSet;
      });

      onAreaSelect(areaId, areaSize || 0, isChecked);

      const polygon = polygonLayersRef.current[areaId];
      if (polygon && mapInstanceRef.current) {
        mapInstanceRef.current.fitBounds(polygon.getBounds(), {
          padding: [10, 10],
          animate: true,
        });
      }
    },
    [onAreaSelect],
  );

  const mapSideBarToggle = (
    e: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>,
  ) => {
    e.preventDefault();
    setIsSidebarVisible((prev) => !prev);
  };

  const selectTotalArea = (e: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = e.target.checked;
    if (isChecked) {
      const allAreaIds = propertyAreas.map((area) => area.id);
      setSelectedAreas(new Set(allAreaIds));
      const totalSize = propertyAreas.reduce(
        (sum, area) => sum + (area.size || 0),
        0,
      );
      onAreaSizeCalculate?.(totalSize);
    } else {
      setSelectedAreas(new Set());
      onAreaSizeCalculate?.(0);
    }
  };

  const getMapContainerWidth = () => {
    if (isMobile) {
      return "100%";
    }
    return isSidebarVisible ? "calc(100% - 300px)" : "100%";
  };

  const getMapContainerHeight = () => {
    if (isSidebarVisible) {
      return "80vh";
    } else {
      return "50vh";
    }
  };

  const zoomOutInToggle = (
    e:
      | React.MouseEvent<HTMLElement | SVGSVGElement>
      | React.KeyboardEvent<HTMLElement>,
  ) => {
    e.preventDefault();
    setIsLeftSidebarVisible((prev) => !prev);
    onToggleLeftSidebar?.();
    if (mapInstanceRef.current) {
      mapInstanceRef.current.setView(center, zoom);
      const allPolygons = Object.values(polygonLayersRef.current);
      if (allPolygons.length > 0) {
        const group = new L.FeatureGroup(allPolygons);
        setTimeout(() => {
          mapInstanceRef.current?.fitBounds(group.getBounds(), {
            padding: [20, 20],
            animate: true,
          });
          mapInstanceRef.current?.invalidateSize({ animate: false });
        }, 300);
      } else {
        setTimeout(() => {
          mapInstanceRef.current?.invalidateSize({ animate: false });
        }, 300);
      }
    }
  };
  return (
    <div
      className="interactive-map-wrapper"
      style={{
        display: "flex",
        flexDirection: isMobile ? "column" : "row",
        height: isMobile ? getMapContainerHeight() : "70vh",
        width: "100%",
      }}
    >
      <div
        className="map-container"
        style={{
          width: isMobile ? "100%" : getMapContainerWidth(),
          transition: "width 0.3s ease-in-out",
        }}
      >
        <div ref={mapRef} className="map-container-inner">
          {isLoading && (
            <div className="map-loading">
              <div className="loading-spinner"></div>
              <span>Loading Satellite Map...</span>
            </div>
          )}
          <div className="zoom-sidebar-controls">
            {isLeftSidebarVisible ? (
              <ZoomOutMap
                className="map-zoom-icon"
                onClick={(e) => zoomOutInToggle(e)}
                style={{ color: "#111928", cursor: "pointer" }}
              />
            ) : (
              <ZoomInMap
                className="map-zoom-icon"
                onClick={(e) => zoomOutInToggle(e)}
                style={{ color: "#111928", cursor: "pointer" }}
              />
            )}
            <button
              className={`map-sidebar-icon ${isSidebarVisible ? "active" : ""}`}
              onClick={(e) => mapSideBarToggle(e)}
            >
              <img
                src="/us/en-us/_assets/images/lawn-area.svg"
                alt="Lawn Area"
                width={24}
                height={24}
              />
            </button>
          </div>
        </div>
      </div>

      <div
        className={`area-list ${isSidebarVisible ? "visible" : "hidden"}`}
        style={{
          width: (() => {
            if (isMobile) {
              return "100%";
            }
            return isSidebarVisible ? "270px" : "0px";
          })(),
          height: (() => {
            if (isMobile) {
              return isSidebarVisible ? "35vh" : "0";
            }
            return "100%";
          })(),
          position: "relative",
          transition: "height 0.3s ease-in-out, width 0.3s ease-in-out",
          overflow: "hidden",
        }}
      >
        <Paper
          sx={{
            width: "100%",
            height: "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div className="lawn-area-header">
            <div className="lawn-area-header-content">
              <img
                src="/us/en-us/_assets/images/ruler-dimension.png"
                alt="Lawn Icon"
                width={24}
                height={24}
                style={{ margin: "8px" }}
              />
              <div className="total-area-info">
                <h2 id="lawn-area-heading" aria-level={2}>
                  Lawn Area
                </h2>
                <span>
                  Size:{" "}
                  {Array.from(selectedAreas)
                    .reduce((sum, id) => {
                      const area = propertyAreas.find((area) => area.id === id);
                      return sum + (area?.size || 0);
                    }, 0)
                    .toLocaleString()}{" "}
                  sq ft
                </span>
              </div>
            </div>
          </div>

          <TableContainer sx={{ flex: 1, overflow: "auto" }}>
            <Table stickyHeader aria-label="sticky table">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ padding: "8px 4px", minWidth: "40px" }}>
                    <Checkbox
                      size="small"
                      checked={
                        propertyAreas.length > 0 &&
                        selectedAreas.size === propertyAreas.length
                      }
                      onChange={selectTotalArea}
                    />
                  </TableCell>
                  <TableCell sx={{ padding: "8px 4px" }}>LAWN AREA</TableCell>
                  <TableCell align="right" sx={{ padding: "8px 4px" }}>
                    SIZE (SQ FT)
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {areasWithLabels.map((area) => (
                  <TableRow key={area.id}>
                    <TableCell sx={{ padding: "4px" }}>
                      <Checkbox
                        size="small"
                        checked={selectedAreas.has(area.id)}
                        onChange={(e) =>
                          handleAreaSelect(area.id, area.size, e)
                        }
                      />
                    </TableCell>
                    <TableCell
                      component="th"
                      scope="row"
                      sx={{ padding: "4px", fontSize: "0.875rem" }}
                    >
                      {area.mapLabel} - {area.label}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{ padding: "4px", fontSize: "0.875rem" }}
                    >
                      {area.size || 0}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </div>
    </div>
  );
};

export default React.memo(InteractiveMap);
